import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { filter, map } from 'lodash';

import { useBoundStore } from 'store';
import {
  useItemsToPrint,
  useShouldPrintAll,
} from 'store/DocumentPrint';
import { setLayoutTempState } from 'store/Layout';
import { setRenvoiMode } from 'store/DocumentSearch';
import { routes } from 'config';
import { addQuery, parseQuery } from 'helpers/query';
import { sanitizeFootnote } from 'helpers/html';
import { RenvoiMode } from 'clients/documents/documentsClient.types';
import { EXPORT_FORMATS } from 'shared/components/dialog/PrintDialog/PrintDialog';
import { Document } from 'views/LoggedIn/Document/Document';
import { downloadDocx } from 'views/LoggedIn/DocumentPrintPreview/downloadDocx';

export const DocumentPrintPreview = () => {
  const [isPrinting, setIsPrinting] = useState(false);
  const itemsToPrint = useItemsToPrint();
  const shouldPrintAll = useShouldPrintAll();
  const { layoutTempState } = useBoundStore((state) => ({
    layoutTempState: state.layoutTempState,
  }));
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = parseQuery(location.search);
    const modeFromUrl = params['renvoi-mode'] as RenvoiMode;

    if (modeFromUrl) {
      setRenvoiMode(modeFromUrl);
    }
  }, [location.search]);

  useEffect(() => {
    if (isPrinting) {
      const params = parseQuery(location.search);
      if (params['format'] === EXPORT_FORMATS.DOCX) {
        downloadDocx(params['renvoi-mode'] as string);
      } else {
        window.print();
      }
      setLayoutTempState({
        ...layoutTempState,
        footnotes: [],
      });
      navigate({
        pathname: routes.document,
        search: addQuery('', {
          slug: params.slug,
          'draft-secret': params['draft-secret'],
          'important-only': params['important-only'],
          'section-id': params['section-id'],
        }),
      });
    }
  }, [isPrinting, layoutTempState, location, navigate, setIsPrinting]);

  const showOnlyItemsToPrint = useCallback(() => {
    const documentChildItems = window.document.querySelectorAll('.print-preview-container .document-child-item');

    documentChildItems.forEach((childItem) => childItem.classList.add('hidden'));

    Object.keys(itemsToPrint || {})?.forEach((key) => {
      if (itemsToPrint?.[key]) {
        document.getElementById(key)?.classList.remove('hidden');
      }
    });
  }, [itemsToPrint]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!shouldPrintAll) {
        showOnlyItemsToPrint();
      }

      const allReferences = window.document.querySelectorAll(
        '.print-document-container .reference-container sup',
      );

      const visibleReferences = filter(allReferences, (referenceElement) => {
        return !referenceElement.closest('.document-child-item.hidden');
      });

      (visibleReferences as Element[]).forEach((referenceElement, index) => {
        referenceElement.textContent = index + 1 + '';
      });

      const currentLayoutState = useBoundStore.getState().layoutTempState;

      setLayoutTempState({
        ...currentLayoutState,
        footnotes: map(visibleReferences as Element[], (element, index) => ({
          number: index + 1,
          text: sanitizeFootnote(element.getAttribute('data-reference-text') || ''),
          is_renvoi: element.getAttribute('data-is-renvoi') === 'true',
          is_full_renvoi: element.getAttribute('data-is-full-renvoi') === 'true',
          is_parent_full_renvoi: element.getAttribute('data-is-parent-full-renvoi') === 'true',
        })),
      });

      setIsPrinting(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [shouldPrintAll, showOnlyItemsToPrint, setIsPrinting]);

  return (
    <Box className="print-preview-container">
      <Document isPrintMode isPrintPreview/>
    </Box>
  );
};